import { Component, OnInit, OnDestroy, inject } from '@angular/core';

import { ToastComponent } from '../shared/toast/toast.component';
import { AuthService } from '../services/auth.service';
import { FileService } from '../services/file.service';
import { RuleService } from '../services/rule.service';
import { FeedbackService } from '../services/feedback.service';
import { Rule } from '../shared/models/rule.model';
import { Feedback } from '../shared/models/feedback.model';
import { ee, profilePics } from '../shared/constants';
import { removeTooltipElement } from '../shared/functions';
import { finalize } from 'rxjs/operators';

@Component({
  selector: 'app-admin',
  templateUrl: './admin.component.html',
  standalone: false
})
export class AdminComponent implements OnInit, OnDestroy {
  auth = inject(AuthService);
  toast = inject(ToastComponent);
  private fileService = inject(FileService);
  private ruleService = inject(RuleService);
  private feedbackService = inject(FeedbackService);


  profilePics = profilePics;
  isLoading = {
    rules: true,
    feedback: true
  };
  rules: Rule[] = [];
  feedback: Feedback[];
  logs = {};
  logNames = [
    { name: 'pm2_app_out', lines: 1000 },
    { name: 'pm2_app_error', lines: 100 },
    // { name: 'pm2_stream_out', lines: 1000 },
    // { name: 'pm2_stream_error', lines: 500 },
    { name: 'crons_pm2check', lines: 25 },
    { name: 'crons_mongodb_backup', lines: 500 },
    { name: 'crons_ocr', lines: 500 },
    { name: 'crons_alert', lines: 1000 },
    { name: 'crons_update_numofposts', lines: 5000 },
    { name: 'crons_timeline', lines: 5000 },
    { name: 'crons_timeline_apify', lines: 5000 },
    { name: 'crons_metrics', lines: 2000 },
    { name: 'crons_contracts', lines: 100 },
    { name: 'crons_lastActivity', lines: 250 },
    { name: 'crons_camera', lines: 500 },
    { name: 'crons_senato', lines: 500 },
    { name: 'crons_eu_seduta', lines: 100 },
    { name: 'crons_region_sources', lines: 1000 },
    { name: 'crons_other_sources', lines: 1000 },
    { name: 'crons_update_stakeholders_camera', lines: 5000 },
    { name: 'crons_update_stakeholders_senato', lines: 5000 },
    { name: 'crons_update_stakeholders_eu', lines: 5000 },
  ];
  currentContent = 'logs';
  menuItems = [
    { name: 'Logs', link: 'logs', icon: 'list' },
    { name: 'Queries', link: 'queries', icon: 'code' },
    { name: 'Feedback', link: 'feedback', icon: 'commenting' }
  ];

  ngOnInit(): void {
    this.getRules();
    this.getFeedbacks();
    this.getLogs();
    ee.on('changeContent', (data: string) => this.currentContent = data);
    ee.on('getRules', () => this.getRules());
  }

  ngOnDestroy(): void {
    ee.off('changeContent', () => {});
    ee.off('getRules', () => {});
    removeTooltipElement();
  }

  getRules(): void {
    this.isLoading.rules = true;
    this.ruleService.getAll()
      .pipe(finalize(() => this.isLoading.rules = false))
      .subscribe({
        next: res => this.rules = res,
        error: err => this.toast.setMessage(err.error.message, 'danger')
      });
  }

  updateRule(rule: Rule): void {
    this.isLoading.rules = true;
    this.ruleService.update(rule)
      .pipe(finalize(() => this.getRules()))
      .subscribe({
        next: res => this.toast.setMessage('Rule updated successfully.', 'success'),
        error: err => this.toast.setMessage(err.error.message, 'danger')
      });
  }

  deleteRule(rule: Rule): void {
    if (window.confirm(`Are you sure you want to delete ${rule.id}?`)) {
      this.isLoading.rules = true;
      this.ruleService.delete(rule)
        .pipe(finalize(() => this.getRules()))
        .subscribe({
          next: res => this.toast.setMessage('Rule deleted successfully.', 'success'),
          error: err => this.toast.setMessage(err.error.message, 'danger')
        });
    }
  }

  getFeedbacks(): void {
    this.isLoading.feedback = true;
    this.feedbackService.getAll()
      .pipe(finalize(() => {
        this.isLoading.feedback = false;
        removeTooltipElement();
      }))
      .subscribe({
        next: res => this.feedback = res,
        error: err => this.toast.setMessage(err.error.message, 'danger')
      });
  }

  updateFeedback(feedback: Feedback): void {
    this.feedbackService.update({ _id: feedback._id, status: 'done' })
      .pipe(finalize(() => this.getFeedbacks()))
      .subscribe({
        next: res => this.toast.setMessage('Feedback updated successfully.', 'success'),
        error: err => this.toast.setMessage(err.error.message, 'danger')
      });
  }

  deleteFeedback(feedback: Feedback): void {
    if (window.confirm(`Are you sure you want to delete ${feedback._id}?`)) {
      this.feedbackService.delete(feedback)
        .pipe(finalize(() => this.getFeedbacks()))
        .subscribe({
          next: res => this.toast.setMessage('Feedback deleted successfully.', 'success'),
          error: err => this.toast.setMessage(err.error.message, 'danger')
        });
    }
  }

  getLogs(): void {
    for (const log of this.logNames) {
      this.logs[log.name] = this.fileService.getLog(log.name, log.lines);
    }
  }

  getLog(logName: string, logLines: number): void {
    this.logs[logName] = this.fileService.getLog(logName, logLines);
  }

  clearLog(logName: string): void {
    if (window.confirm(`Are you sure you want to clear ${logName}.log?`)) {
      this.fileService.clearLog(logName)
        .pipe(finalize(() => this.getLog(logName, 100)))
        .subscribe({
          next: res => this.toast.setMessage('Log cleared successfully.', 'success'),
          error: err => this.toast.setMessage(err.error.message, 'danger')
        });
    }
  }

}
